import { yupResolver } from '@hookform/resolvers/yup';
import { Edit } from '@mui/icons-material';
import { Button, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

export type CompanyBasicSettingsFormData = {
  name: string;
  legalName?: string;
  address?: string;
  domain?: string;
  alternativeDomains?: string[];
};

type CompanyBasicSettingsFormProps = Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  'onSubmit'
> & {
  initialData: CompanyBasicSettingsFormData;
  onSubmit: (data: CompanyBasicSettingsFormData) => Promise<void>;
  isLoading?: boolean;
};

type FormValues = {
  name: string;
  legalName?: string;
  address?: string;
  domain?: string;
  alternativeDomains?: string;
};

export function CompanyBasicSettingsForm({
  initialData,
  onSubmit,
  isLoading = false,
  ...props
}: CompanyBasicSettingsFormProps) {
  const [isEditing, setIsEditing] = useState(false);

  const CompanyBasicSettingsFormSchema = yup.object().shape({
    name: yup.string().required('Company name is required'),
    legalName: yup.string(),
    address: yup.string(),
    alternativeDomains: yup.string(),
  });

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<FormValues>({
    disabled: isLoading,
    values: {
      name: initialData.name ?? '',
      legalName: initialData.legalName ?? '',
      address: initialData.address ?? '',
      domain: initialData.domain ?? '',
      alternativeDomains: initialData.alternativeDomains?.join(',') ?? '',
    },
    resolver: yupResolver(CompanyBasicSettingsFormSchema),
  });

  const handleFormSubmit = async (formData: FormValues) => {
    await onSubmit({
      ...formData,
      alternativeDomains:
        formData.alternativeDomains
          ?.split(',')
          .map(d => d.trim())
          .filter(Boolean) || [],
    });
    setIsEditing(false);
  };

  return (
    <form
      {...props}
      onSubmit={handleSubmit(handleFormSubmit)}
      className={`flex flex-col w-full gap-4 ${props.className ?? ''}`}
    >
      <div className="flex justify-between items-center mb-1">
        <div>
          <Typography variant="h3" component="h2">
            Company Information
          </Typography>
          <Typography variant="body1" color="text.secondary" className="mt-1">
            Review and Update Company Details.
          </Typography>
        </div>
        <Button
          variant="outlined"
          startIcon={<Edit />}
          onClick={() => setIsEditing(true)}
          className={isEditing ? 'invisible' : 'visible'}
        >
          Edit information
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-x-8 gap-y-4">
        <div>
          <Typography variant="body2" className="font-semibold mb-1">
            Company Name
          </Typography>
          {isEditing ? (
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  id="name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  placeholder="Company Name"
                  aria-label="Company Name"
                />
              )}
            />
          ) : (
            <Typography color="text.secondary">
              {initialData.name ?? '-'}
            </Typography>
          )}
        </div>

        <div>
          <Typography variant="body2" className="font-semibold mb-1">
            Company Legal Name
          </Typography>
          {isEditing ? (
            <Controller
              name="legalName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  error={!!errors.legalName}
                  helperText={errors.legalName?.message}
                  fullWidth
                  aria-label="Company Legal Name"
                  placeholder="Company Legal Name"
                />
              )}
            />
          ) : (
            <Typography color="text.secondary">
              {initialData.legalName ?? '-'}
            </Typography>
          )}
        </div>

        <div>
          <Typography variant="body2" className="font-semibold mb-1">
            Company Address
          </Typography>
          {isEditing ? (
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  fullWidth
                  aria-label="Company Address"
                  placeholder="Company Address"
                />
              )}
            />
          ) : (
            <Typography color="text.secondary">
              {initialData.address ?? '-'}
            </Typography>
          )}
        </div>

        <div>
          <Typography variant="body2" className="font-semibold mb-1">
            Company Domain
          </Typography>
          {isEditing ? (
            <Controller
              name="domain"
              control={control}
              disabled
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  error={!!errors.domain}
                  helperText={errors.domain?.message}
                  fullWidth
                  aria-label="Company Domain"
                  placeholder="Company Domain"
                />
              )}
            />
          ) : (
            <Typography color="text.secondary">
              {initialData.domain ?? '-'}
            </Typography>
          )}
        </div>

        <div>
          <Typography variant="body2" className="font-semibold mb-1">
            Company Alternative Domains (comma separated)
          </Typography>
          {isEditing ? (
            <Controller
              name="alternativeDomains"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  error={!!errors.alternativeDomains}
                  helperText={errors.alternativeDomains?.message}
                  fullWidth
                  aria-label="Company Alternative Domains"
                  placeholder="Company Alternative Domains"
                />
              )}
            />
          ) : (
            <Typography color="text.secondary">
              {initialData.alternativeDomains?.join(', ') ?? '-'}
            </Typography>
          )}
        </div>
      </div>
      {isEditing && (
        <div className="flex gap-2 mt-4">
          <Button
            variant="contained"
            size="small"
            type="submit"
            disabled={isLoading}
            loading={isSubmitting}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsEditing(false)}
            disabled={isLoading || isSubmitting}
          >
            Cancel
          </Button>
        </div>
      )}
    </form>
  );
}
